import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../api/client.js";
import { useAuth } from "../../auth/AuthContext.js";

/**
 * FIX-057 §16 (owner t1835u 03.11.2026 23:18 IL: "a button next to every automation on REVIEW so I can stop it if I want"). One slim card right under each
 * existing automation bar, in the Instagram bar's pattern (`card auto-run-bar`, headline + muted line + Turn off / Turn on with a confirm, 15 s refresh, 403 hides it).
 * The existing bars are not changed. Each switch uses the automation's EXISTING, persisted owner switch (no new server state):
 *  - DR -> RE auto-production: `GET /re/:t/auto/status` (settings.paused) + `POST /re/:t/auto/pause {paused}` (re-auto settings table). Off = no new run starts and an
 *    active run waits before its next step (nothing already submitted is cancelled); On = it continues.
 *  - Telegram daily publish, per channel: `GET /images/:t/telegram-publish/status` (channels[].autoPublishEnabled) + `POST /images/:t/telegram-publish/channels/:key/auto
 *    {enabled}` (telegram_publish_channel_settings). Off = the 19:00 NY automatic post of that channel is not made; a post already sending is not interrupted.
 * The Instagram auto-posting bar already has its own button (FIX-057 §10).
 */

const REFRESH_MS = 15_000;
const errMsg = (e: unknown, fallback: string): string => { const b = e instanceof ApiError ? (e.body as { message?: string; error?: string } | null) : null; return b?.message ?? b?.error ?? fallback; };

interface AutoStatus { readonly enabled: boolean; readonly settings?: { readonly paused: boolean; readonly pausedReason: string | null; readonly updatedAt: string | null } | null; readonly activeRun: { readonly id: string } | null }

/** Pure: the auto-production switch line (tested directly). */
export function reAutoSwitchText(s: AutoStatus): { readonly headline: string; readonly detail: string; readonly on: boolean } {
  const paused = s.settings?.paused === true;
  const on = !paused;
  const headline = `Auto-production (DR → RE) · ${!s.enabled ? "Off (server setting)" : on ? "On" : "Off"}`;
  const detail = !s.enabled ? "Switched off on the server." : on ? "Runs daily and from the buttons." : `Stopped${s.settings?.pausedReason ? `: ${s.settings.pausedReason}` : ""}${s.activeRun ? " · the active run waits" : ""}`;
  return { headline, detail, on };
}

export function ReviewsReAutoSwitch({ tenantId }: { readonly tenantId: string }) {
  const { csrfToken } = useAuth();
  const [st, setSt] = useState<AutoStatus | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const off = useRef(false);
  useEffect(() => {
    let live = true; off.current = false;
    const load = async (): Promise<void> => {
      if (off.current) return;
      try { const s = await api.get<AutoStatus>(`/re/${tenantId}/auto/status`); if (live && s && typeof s === "object") setSt(s); }
      catch (e) { if (live && e instanceof ApiError && (e.status === 403 || e.status === 404 || e.status === 503)) { off.current = true; setHidden(true); } }
    };
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => { live = false; clearInterval(t); };
  }, [tenantId]);
  if (hidden || !st) return null;
  const t = reAutoSwitchText(st);
  const toggle = async (): Promise<void> => {
    const q = t.on
      ? "Turn auto-production OFF? No new run starts; an active run waits before its next step (nothing already submitted is cancelled)."
      : "Turn auto-production ON? Runs start again and a waiting run continues.";
    if (!window.confirm(q)) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.post<{ settings: AutoStatus["settings"] }>(`/re/${tenantId}/auto/pause`, csrfToken, t.on ? { paused: true, reason: "stopped by the owner on the Review screen" } : { paused: false });
      setSt({ ...st, settings: r?.settings ?? { paused: t.on, pausedReason: null, updatedAt: null } });
      setMsg(t.on ? "Switched off." : "Switched on.");
    } catch (e) { setMsg(errMsg(e, "The switch failed.")); } finally { setBusy(false); }
  };
  return (
    <div className="card auto-run-bar" data-testid="reviews-reauto-switch" data-state={t.on ? "on" : "off"} role="status" aria-live="polite">
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <strong data-testid="reviews-reauto-switch-headline">{t.headline}</strong>
        <span className="muted" data-testid="reviews-reauto-switch-detail">{t.detail}</span>
        {st.enabled ? (
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button type="button" className={t.on ? "danger" : undefined} data-testid="reviews-reauto-switch-toggle" disabled={busy} onClick={() => void toggle()}>{t.on ? "Turn off" : "Turn on"}</button>
          </span>
        ) : null}
      </div>
      {msg ? <p className="muted" style={{ margin: 0 }}>{msg}</p> : null}
    </div>
  );
}

interface TgStatusLite { readonly autoPublishEnabled: boolean; readonly channels: readonly { readonly key: string; readonly title: string; readonly autoPublishEnabled: boolean }[] }

export function ReviewsTelegramAutoSwitch({ tenantId }: { readonly tenantId: string }) {
  const { csrfToken } = useAuth();
  const [st, setSt] = useState<TgStatusLite | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const off = useRef(false);
  useEffect(() => {
    let live = true; off.current = false;
    const load = async (): Promise<void> => {
      if (off.current) return;
      try { const s = await api.get<TgStatusLite>(`/images/${tenantId}/telegram-publish/status`); if (live && s && Array.isArray(s.channels)) setSt(s); }
      catch (e) { if (live && e instanceof ApiError && (e.status === 403 || e.status === 404 || e.status === 503)) { off.current = true; setHidden(true); } }
    };
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => { live = false; clearInterval(t); };
  }, [tenantId]);
  if (hidden || !st) return null;
  const toggle = async (key: string, title: string, on: boolean): Promise<void> => {
    const q = on ? `Turn the daily automatic post OFF for ${title}? No automatic post is made; a post already sending is not interrupted.` : `Turn the daily automatic post ON for ${title}?`;
    if (!window.confirm(q)) return;
    setBusy(true); setMsg(null);
    try {
      await api.post(`/images/${tenantId}/telegram-publish/channels/${encodeURIComponent(key)}/auto`, csrfToken, { enabled: !on });
      setSt({ ...st, channels: st.channels.map((c) => (c.key === key ? { ...c, autoPublishEnabled: !on } : c)) });
      setMsg(on ? `${title}: switched off.` : `${title}: switched on.`);
    } catch (e) { setMsg(errMsg(e, "The switch failed.")); } finally { setBusy(false); }
  };
  return (
    <div data-testid="reviews-telegram-switch">
      {st.channels.map((c) => {
        const on = c.autoPublishEnabled;
        return (
          <div key={c.key} className="card auto-run-bar" data-testid={`reviews-telegram-switch-${c.key}`} data-state={on ? "on" : "off"} role="status" aria-live="polite">
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <strong data-testid={`reviews-telegram-switch-headline-${c.key}`}>Telegram daily post · {c.title} · {!st.autoPublishEnabled ? "Off (server setting)" : on ? "On" : "Off"}</strong>
              {st.autoPublishEnabled ? (
                <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <button type="button" className={on ? "danger" : undefined} data-testid={`reviews-telegram-switch-toggle-${c.key}`} disabled={busy} onClick={() => void toggle(c.key, c.title, on)}>{on ? "Turn off" : "Turn on"}</button>
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
      {msg ? <p className="muted" style={{ margin: 0 }}>{msg}</p> : null}
    </div>
  );
}
